/**
 * RESPONSIBILITY: what a @List column is, and what drawing one needs.
 *
 * Types only, no rendering. Separate from the cells themselves so a cell that lives in
 * its own file can import this without importing the lookup that will contain it.
 *
 * A cell draws a VALUE, never its own name. The row draws every column's label from the
 * annotation — see list.tsx — so nothing here carries one.
 */
/** One column of a repeatable row, as described by the annotation on the @Row class. */
export interface ItemField {
  key: string;
  label: string;
  kind: string;
  description?: string;
  maxLength?: number;
  /** stringlist — how many values the column may hold. */
  maxItems?: number;
  rows?: number;
  /** img */
  folder?: string;
  maxSizeMb?: number;
  /** Pixel size this slot wants. Advice shown on the control — see FieldMeta.size. */
  size?: string;
  /** What this column starts as in a row somebody has just added. */
  default?: unknown;
  /** date */
  time?: boolean;
  /** num */
  min?: number;
  max?: number;
  integer?: boolean;
  /** choice */
  in?: string;
  multiple?: boolean;
  fixed?: readonly string[];
  /** Draw it as native radios or checkboxes rather than a select or chips. */
  as?: 'radio' | 'checkbox';
  /**
   * list — the columns of the rows THIS column holds. A repeatable inside a repeatable
   * is described exactly as the outer one is, which is what lets one control draw both.
   */
  itemFields?: ItemField[];
  /** list */
  minItems?: number;
  /**
   * Collapsible panel this column is edited in. Columns naming the same one are gathered
   * into it, where the first of them was declared — see row-fields.tsx.
   */
  group?: string;
  /** That panel starts closed. Declared on the first column of the group. */
  collapsed?: boolean;
  /**
   * Ask for this column only when a sibling in the same row holds one of these values.
   * Presentation only — see ShowWhen in core/decorators/types.ts.
   */
  showWhen?: { field: string; is: readonly string[] };
}

export interface CellProps {
  field: ItemField;
  value: unknown;
  onChange: (v: unknown) => void;
  /**
   * Goes on the control this cell exists to draw. The row's caption is a real
   * `<label htmlFor>`, so a cell that drops this has a name on screen and none to a
   * screen reader — which is what the `placeholder` these labels replaced amounted to.
   */
  id: string;
  enumVals?: string[];
  /** Row position. The image column needs it to name the slot it uploads into. */
  index: number;
  /** The section document. A cell with `in` resolves its vocabulary against the root. */
  root: Record<string, unknown>;
  /** The row this cell sits in, for an `in` path that starts `./`. */
  row: Record<string, unknown>;
}

/**
 * How a cell is drawn, and how wide it wants to be — together, because both answers come
 * from the same annotation and splitting them is how they drift apart.
 */
export interface Cell {
  render: (p: CellProps) => JSX.Element;
  /** flex-basis for the cell. The row is a wrapping flex line. */
  basis: (f: ItemField) => string;
  /**
   * flex-grow. Defaults to 1 — take a share of whatever the line has spare.
   * A checkbox is the size it is, and stretching one just moves it away from its
   * own label, so it declares 0 here rather than being named in the stylesheet.
   */
  grow?: number;
}

/** Every cell renders a value it did not choose the type of. */
export const str = (v: unknown): string => (v === undefined || v === null ? '' : String(v));
